import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { localizePath, parseLocalizedPath } from "../i18n";
import { initializeAnalytics } from "../lib/analytics";
import { LOCALE_BY_KEY } from "../i18n/locale-config";
import { getNotFoundCopy, notFoundDocumentTitle } from "../i18n/not-found";

function NotFoundComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { locale } = parseLocalizedPath(pathname);
  const copy = getNotFoundCopy(locale);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">{copy.title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{copy.body}</p>
        <div className="mt-6">
          <a
            href={localizePath("/", locale)}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {copy.action}
          </a>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/en-ca"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: ({ matches, match }) => {
    const isNotFound = Boolean(
      match.globalNotFound ||
      matches.some((entry) => entry.status === "notFound" || entry.globalNotFound),
    );

    // Search-engine ownership verification. Tokens are intentionally public
    // metadata (that is their whole purpose) and are configured per
    // environment — never hardcoded — so previews don't claim production.
    const googleVerification = import.meta.env.VITE_GOOGLE_SITE_VERIFICATION;
    const bingVerification = import.meta.env.VITE_BING_SITE_VERIFICATION;

    const meta = [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      ...(googleVerification
        ? [{ name: "google-site-verification", content: googleVerification }]
        : []),
      ...(bingVerification ? [{ name: "msvalidate.01", content: bingVerification }] : []),
      { name: "author", content: "Revolin Roofing" },
      { name: "theme-color", content: "#0a1929" },
      { property: "og:site_name", content: "Revolin Roofing" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      ...(isNotFound
        ? (() => {
            // Child-route head is skipped when notFound bubbles to root — locale comes from the matched path.
            const pathname =
              [...matches].reverse().find((entry) => entry.pathname && entry.pathname !== "/")
                ?.pathname ||
              match.pathname ||
              "/";
            const { locale } = parseLocalizedPath(pathname);
            const copy = getNotFoundCopy(locale);
            return [
              { title: notFoundDocumentTitle(locale) },
              { name: "description", content: copy.body },
              { name: "robots", content: "noindex, nofollow" },
            ];
          })()
        : []),
    ];

    return {
      meta,
      links: [
        {
          rel: "stylesheet",
          href: appCss,
        },
        // Fonts are self-hosted (see src/fonts.css) — no render-blocking
        // Google Fonts stylesheet or preconnects needed.
        { rel: "icon", href: "/RevolinGroup-new-logo.png", type: "image/png" },
        { rel: "apple-touch-icon", href: "/RevolinGroup-new-logo.png" },
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { locale } = parseLocalizedPath(pathname);
  const localeDef = LOCALE_BY_KEY[locale];

  return (
    <html lang={localeDef.bcp47} dir={localeDef.dir}>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    initializeAnalytics();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}
